import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { MICROSERVICES } from '@shared/constants/microservices';
import { catchError, map, Observable } from 'rxjs';
import {
    BulkCreationReport,
    BulkDeletionResponse,
    Vehicle,
    VehicleRequest,
    VehiclesBlocked,
    VehiclesResponse,
} from '../domain/vehicle.model';
import { VehicleRepository } from '../domain/vehicle.repository';

@Injectable({
    providedIn: 'root',
})
export class VehiclesApiService implements VehicleRepository {
    readonly http = inject(HttpClient);

    private readonly endpoint = '/vehicles';

    create(vehicleDTO: VehicleRequest): Observable<Vehicle> {
        return this.http.post<Vehicle>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}`, vehicleDTO).pipe(
            catchError((error) => {
                throw error;
            }),
            map((resp: Vehicle) => {
                localStorage.setItem('vehicle', JSON.stringify({ vid: resp['id'] }));
                return resp;
            })
        );
    }

    getVehicle(id: number): Observable<Vehicle> {
        return this.http.get<Vehicle>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/${id}`);
    }

    getVehicles(page: number, pageSize: number, search?: string): Observable<VehiclesResponse> {
        const params: Record<string, string | number> = { page, pageSize };
        if (search) {
            params['filterByLicensePlateOrNickname'] = search;
        }
        return this.http.get<VehiclesResponse>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}`, { params });
    }

    deleteVehicle(id: number): Observable<void> {
        return this.http.delete<void>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/${id}`);
    }

    retryVehicle(id: number): Observable<Vehicle> {
        return this.http.post<Vehicle>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/${id}/retry`, {}).pipe(
            catchError((error) => {
                throw error;
            }),
            map((resp: Vehicle) => {
                return resp;
            })
        );
    }

    updateEndDate(id: number, endDate: string): Observable<Vehicle> {
        return this.http
            .patch<Vehicle>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/${id}`, {
                endDate: endDate,
            })
            .pipe(
                catchError((error) => {
                    throw error;
                }),
                map((resp: Vehicle) => {
                    return resp;
                })
            );
    }

    updateNickName(id: number, nickname: string): Observable<Vehicle> {
        return this.http
            .patch<Vehicle>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/${id}`, {
                nickname: nickname,
            })
            .pipe(
                catchError((error) => {
                    throw error;
                }),
                map((resp: Vehicle) => {
                    return resp;
                })
            );
    }

    getVehiclesBlocked(): Observable<VehiclesBlocked> {
        return this.http.get<VehiclesBlocked>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/blocked`);
    }

    uploadCsv(file: File): Observable<BulkCreationReport> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<BulkCreationReport>(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/csv`, formData);
    }

    bulkDelete(file: File, persist: boolean): Observable<BulkDeletionResponse> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<BulkDeletionResponse>(
            `${environment.url}${MICROSERVICES.vehicles}${this.endpoint}/deactivation-batches?persistChanges=${persist}`,
            formData
        );
    }
    exportVehiclesCsv(): Observable<HttpResponse<Blob>> {
        return this.http.get(`${environment.url}${MICROSERVICES.vehicles}${this.endpoint}`, {
            headers: { Accept: 'text/csv' },
            observe: 'response',
            responseType: 'blob',
        });
    }
}
